import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Typography,
  CircularProgress,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
} from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { useDispatch, useSelector } from 'react-redux';
import { useUser } from '@/providers';
import { useNavigate } from 'react-router';

import {
  deleteDataConnector,
  fetchDataConnectors,
} from '../../../../actions/dataConnectors';

import DataConnectorGridToolbar from './DataConnectorGridToolbar';
import DATA_CONNECTOR_TYPES from '../DataConnectorForm/dataConnectorTypes';
import CopyableGridCell from '@/components/shared/CopyableGridCell';

export default function DataConnectors({ region, autoFetch = true }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useUser();
  const data_connectors = useSelector((state) => state.dataConnectors);
  const [transitioningDataConnectors, setTransitioningDataConnectors] =
    useState([]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});

  useEffect(() => {
    (async () => {
      if (autoFetch && data_connectors === null) {
        dispatch(fetchDataConnectors(region.provider_uuid, region.region_uuid));
        return;
      }
    })();
  }, [dispatch, region, data_connectors, autoFetch]);

  const editDataConnector = useCallback(
    (data_connector) => () => {
      navigate(`/resources/provider/${region.provider_uuid}/region/${region.region_uuid}/data_connector/edit?data_connector=${data_connector.connector_id}`);
    },
    [navigate, region]
  );
  const removeDataConnector = useCallback(
    (data_connector) => async () => {
      setTransitioningDataConnectors((prevTransitioningDataConnectors) => [
        ...prevTransitioningDataConnectors,
        data_connector.connector_id,
      ]);
      await dispatch(
        deleteDataConnector(
          region.provider_uuid,
          region.region_uuid,
          data_connector.connector_id
        )
      );
      setTransitioningDataConnectors((prevTransitioningDataConnectors) =>
        prevTransitioningDataConnectors.filter(
          (id) => id !== data_connector.connector_id
        )
      );
    },
    [dispatch, region]
  );

  const renderStatus = useCallback(
    (params) => {
      if (transitioningDataConnectors.includes(params.id)) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <CircularProgress size={20} />
          </Box>
        );
      } else {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Stack direction='row' spacing={1}>
              <CheckCircleIcon color='success' />
            </Stack>
          </Box>
        );
      }
    },
    [transitioningDataConnectors]
  );

  const renderDetails = useCallback((params) => {
    switch (params.row.type) {
      case DATA_CONNECTOR_TYPES.CUSTOM: {
        return (
          <Typography variant='body' paragraph>
            {params.row.cidr} on {params.row.protocol} port{' '}
            {params.row.port_range}
          </Typography>
        );
      }
      default:
        return <></>;
    }
  }, []);

  const columns = useMemo(
    () => [
      {
        field: 'connector_id',
        headerName: 'ID',
        type: 'string',
        flex: 1.25,
        renderCell: (params) => <CopyableGridCell value={params.value} />,
      },
      {
        field: 'name',
        headerName: 'Name',
        type: 'string',
        flex: 1,
      },
      {
        field: 'status',
        headerName: 'Status',
        renderCell: renderStatus,
        flex: 0.25,
      },
      {
        field: 'type',
        headerName: 'Type',
        type: 'string',
        flex: 0.5,
      },
      {
        headerName: 'Details',
        type: 'string',
        renderCell: renderDetails,
        flex: 1,
      },

      {
        field: 'actions',
        headerName: 'Actions',
        type: 'actions',
        width: 80,
        getActions: (params) => [
          <GridActionsCellItem
            icon={<EditIcon />}
            label='Edit'
            onClick={editDataConnector(params.row)}
            showInMenu
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label='Remove'
            onClick={removeDataConnector(params.row)}
            showInMenu
          />,
        ],
      },
    ],
    [editDataConnector, removeDataConnector, renderStatus, renderDetails]
  );

  const Toolbar = () => (
    <DataConnectorGridToolbar
      region={region}
      disabled={!user.cloudbender_enabled}
    />
  );

  return (
    <Accordion defaultExpanded>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls='panel1a-content'
        id='panel1a-header'
      >
        <Typography variant='h6' paragraph gutterBottom>
          Data Connectors
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <div style={{ width: '100%' }}>
          <div style={{ display: 'flex', height: '100%' }}>
            <div style={{ flexGrow: 1 }}>
              <DataGrid
                autoHeight
                getRowId={(row) => row.connector_id}
                columns={columns}
                rows={data_connectors || []}
                slots={{
                  toolbar: Toolbar,
                }}
                showToolbar
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={(newModel) =>
                  setColumnVisibilityModel(newModel)
                }
              />
            </div>
          </div>
        </div>
      </AccordionDetails>
    </Accordion>
  );
}
